import classNames from "classnames"
import { useCallback, useEffect, useRef, useState } from "react"

import { getLineClampClass } from "@bounce/design-system/src/components/Typography/style"
import type { CommonTypographyProps } from "@bounce/design-system/src/components/Typography/types"
import { useTranslation } from "@bounce/i18n"

const __DEFAULT_ELEMENT__ = "div"

type TruncateProps<E extends React.ElementType = React.ElementType> = {
  as?: E
  children: React.ReactNode
  className?: string
  numberOfLines?: CommonTypographyProps["numberOfLines"]
  onClick?: (isOpen: boolean) => void
  toggleClassName?: string
}

export const Truncate = ({ as, className, children, numberOfLines = 3, onClick, toggleClassName }: TruncateProps) => {
  const { t } = useTranslation("web-components")
  const ref = useRef<HTMLParagraphElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isClamped, setIsClamped] = useState(false)

  const Component = as ?? __DEFAULT_ELEMENT__

  useEffect(() => {
    const element = ref.current
    if (!element) {
      return
    }

    const observer = new ResizeObserver(() => {
      setIsClamped(element.scrollHeight > element.clientHeight)
    })

    observer.observe(element)

    return () => {
      observer.unobserve(element)
    }
  }, [])

  const toggleIsOpen = useCallback(
    (ev: React.MouseEvent<HTMLSpanElement>) => {
      ev.preventDefault()
      setIsOpen(!isOpen)
      onClick?.(!isOpen)
    },
    [isOpen, onClick],
  )

  const shouldShowToggle = (isClamped && !isOpen) || isOpen

  return (
    <>
      <Component
        ref={ref}
        className={classNames(className, !isOpen && getLineClampClass(numberOfLines), "overflow-hidden text-ellipsis")}>
        {children}
      </Component>
      <span
        className={classNames(className, toggleClassName, "block cursor-pointer text-primary-700", {
          hidden: !shouldShowToggle,
          "mt-2": !isOpen,
        })}
        onClick={toggleIsOpen}>
        {isOpen ? t("cmp.truncate.open", "Show less") : t("cmp.truncate.closed", "Show more")}
      </span>
    </>
  )
}
